import { DeleteForever as DeleteForeverIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { Button, Card, CardActions, CardContent, CardHeader, Link, Stack, Typography } from '@mui/material';
import { FunctionComponent, PropsWithChildren, useState } from 'react';
import { signOut } from '../Authentication';
import { useScreenIsNarrow } from '../components/Accessibility';
import { KeyboardShortcutCheckbox } from '../components/admin/KeyboardShortcutCheckbox';
import { EmailSupportLink } from '../components/EmailSupportLink';
import { InlineErrorAlert } from '../components/InlineErrorAlert';
import { NarrowAdminLayout } from '../components/layout/AdminLayout';
import { ResetAppDialog } from '../components/ResetAppDialog';
import { useAuthenticatedUser } from '../contexts/AuthContext';
import { Errors } from '../data/Errors';
import { Environment } from '../util/Environment';

export const SettingsScreen: FunctionComponent = () => {
	const [error, setError] = useState<string | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [resetDialogOpen, setResetDialogOpen] = useState(false);

	const { user, isAdmin } = useAuthenticatedUser();

	async function handleSignOut() {
		setError(undefined);
		setLoading(true);

		try {
			await signOut();
		} catch (err) {
			if (err instanceof Error) {
				setError(err.message);
			} else {
				setError('An error occurred during sign out');
			}
			Errors.report('Error during sign out', err);
		} finally {
			setLoading(false);
		}
	}

	return <NarrowAdminLayout title='Settings'>
		<Stack spacing={4}>
			<Card variant='outlined'>
				<CardHeader title='User' />
				<CardContent>
					<LabelledText label='Name'>{user.displayName ?? 'Not set'}</LabelledText>
					<LabelledText label='Email'>{user.email}</LabelledText>
					<LabelledText label='Role'>{isAdmin ? 'Admin' : 'Facilitator'}</LabelledText>
				</CardContent>
				<CardActions>
					<Button variant="contained" disabled={loading}
						startIcon={<LogoutIcon />}
						onClick={handleSignOut}
					>
						Sign Out
					</Button>
					<InlineErrorAlert error={error} />
				</CardActions>
			</Card>

			<Card variant='outlined'>
				<CardHeader title='Settings' />
				<CardContent>
					<KeyboardShortcutCheckbox />
				</CardContent>
			</Card>

			<Card variant='outlined'>
				<CardHeader title='Information' />
				<CardContent>
					<LabelledText label='App Version'>{Environment.appVersion}</LabelledText>
					<LabelledText label='Support'><EmailSupportLink /></LabelledText>
					<LabelledText label='Credits'>
						<Stack>
							Music from #Uppbeat (free for Creators!)
							<Link href='https://uppbeat.io/t/richard-bodgers/bingo-crazy'>Bingo Crazy by Richard Bodgers</Link>
							<Link href='https://uppbeat.io/t/airstream/game-show'>Game Show by Airstream</Link>
						</Stack>
					</LabelledText>
				</CardContent>
			</Card>

			<Card variant='outlined'>
				<CardHeader title='Reset' />
				<CardContent>
					<Typography variant="body2" color="text.secondary">
						Reset all application data including game configuration, gameplay state, and settings.
						This action cannot be undone. Saved questions and Question Sets will not be affected.
					</Typography>
				</CardContent>
				<CardActions>
					<Button variant="outlined"
						color="error"
						startIcon={<DeleteForeverIcon />}
						onClick={() => setResetDialogOpen(true)}
					>
						Reset Application
					</Button>
				</CardActions>
			</Card>
		</Stack>
		<ResetAppDialog
			open={resetDialogOpen}
			onClose={() => setResetDialogOpen(false)}
			setError={setError} />
	</NarrowAdminLayout>;
};


type LabelledTextProps = PropsWithChildren<{
	label: string;
}>;

const LabelledText: FunctionComponent<LabelledTextProps> = (props) => {
	const vertical = useScreenIsNarrow();

	return <Stack direction={vertical ? 'column' : 'row'}>
		<Typography variant="body2" color="text.secondary" sx={{ minWidth: '100px' }}>
			{props.label}
		</Typography>
		<Typography variant="body1" gutterBottom>
			{props.children}
		</Typography>
	</Stack >;
};
